import type { RateLineProposalListQuery } from '@chukua/common';
import { RATE_LINE_PROPOSAL_KINDS, RATE_LINE_PROPOSAL_STATUSES } from '@chukua/common';
import { Button, type TableColumnsType } from 'antd';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { formatMoney } from '../../../../../shared/policies/format-money';
import { ProposalStatusTag } from './ProposalStatusTag';
import { PROPOSAL_KIND_LABELS, PROPOSAL_STATUS_LABELS } from '../../../policies/rate-card-labels';
import type { RateLineProposalView } from '../../../rate-cards.api';
import { formatLineKey } from '../../../policies/rate-line-key';
import type { ProposalAction } from '../hooks/use-proposal-actions';

interface BuildProposalColumnsArgs {
  list: Pick<ListQueryControls<RateLineProposalListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  /** Edit, submit and withdraw belong to the author alone, so for anyone else they are not rendered at all. */
  canActOn: (proposal: RateLineProposalView) => boolean;
  onAction: (action: ProposalAction) => void;
}

/** Each column's `key` is its `sort` field or filter param in `rateLineProposalListQuerySchema`. */
export function buildProposalColumns({ list, canActOn, onAction }: BuildProposalColumnsArgs): TableColumnsType<RateLineProposalView> {
  return [
    {
      title: 'Line',
      key: 'line',
      render: (_, proposal) => (
        <>
          {formatLineKey(proposal)}
          {proposal.status === 'draft' && proposal.lastDecisionReason && <p className="text-xs text-muted">Sent back: {proposal.lastDecisionReason}</p>}
        </>
      ),
    },
    {
      title: 'Kind',
      key: 'kind',
      filters: RATE_LINE_PROPOSAL_KINDS.map((kind) => ({ text: PROPOSAL_KIND_LABELS[kind], value: kind })),
      filteredValue: list.filteredValueFor('kind'),
      render: (_, proposal) => PROPOSAL_KIND_LABELS[proposal.kind],
    },
    { title: 'Price', key: 'amount', render: (_, proposal) => formatMoney(proposal.amount) },
    { title: 'Effective from', dataIndex: 'validFrom', key: 'validFrom', sorter: true, sortOrder: list.sortOrderFor('validFrom') },
    {
      title: 'Status',
      key: 'status',
      filters: RATE_LINE_PROPOSAL_STATUSES.map((status) => ({ text: PROPOSAL_STATUS_LABELS[status], value: status })),
      filteredValue: list.filteredValueFor('status'),
      render: (_, proposal) => <ProposalStatusTag status={proposal.status} />,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortOrder: list.sortOrderFor('createdAt'),
      render: (createdAt: string) => new Date(createdAt).toLocaleDateString(),
    },
    {
      title: '',
      key: 'actions',
      render: (_, proposal) =>
        proposal.status === 'draft' &&
        canActOn(proposal) && (
          <div className="flex gap-1">
            <Button size="small" type="link" onClick={() => onAction({ kind: 'edit', proposal })}>
              Edit
            </Button>
            <Button size="small" type="link" onClick={() => onAction({ kind: 'submit', proposal })}>
              Submit
            </Button>
            <Button size="small" type="link" danger onClick={() => onAction({ kind: 'withdraw', proposal })}>
              Withdraw
            </Button>
          </div>
        ),
    },
  ];
}
